import { UI_ACTIONS } from 'store/v1/ui/ui.constants';

import { myToastService } from 'v5/services/front/ToastService/ToastService.connected.js';
import mapAlertError from 'v1/helpers/alertErrors';
import { resourceDrawerGuard } from 'v5/features/resources/resourceDrawerGuard';
import { values } from 'lodash';

/** @typedef {import('v5/core/app-scope/appScope.types').AppScope} AppScope */

/**
 * Modals and drawers render outside their opener, so the opener names the
 * scope they work in: its browsing scope (`appScopeService.useSlugAppScope()`)
 * or an explicit organization scope for an org-wide flow. Without one this
 * throws in development and tests; production logs it and falls back to the
 * organization, as before scopes were required.
 *
 * @param {AppScope | null | undefined} appScope
 * @param {string} opener
 * @returns {AppScope | null}
 */
function requireAppScope(appScope, opener) {
  if (appScope) return appScope;
  const error = new Error(
    `${opener} needs the opener's appScope (browsing scope, or an explicit organization scope)`
  );
  if (import.meta.env.PROD) {
    console.error(error);
    return null;
  }
  throw error;
}

const closeModal = () => {
  return { type: UI_ACTIONS.CLOSE_MODAL };
};

/**
 * @param {string} modalType
 * @param {object | undefined} data
 * @param {AppScope} appScope The scope the modal works in (see {@link requireAppScope}).
 */
const openModal = (modalType, data, appScope) => {
  return {
    type: UI_ACTIONS.OPEN_MODAL,
    modalType,
    data,
    appScope: requireAppScope(appScope, `openModal('${modalType}')`)
  };
};

/**
 * Shows `selectedContact` in the resource drawer. While the drawer holds
 * unsaved changes of another resource it asks first (`resourceDrawerGuard`),
 * and nothing happens when the user keeps editing.
 *
 * @param {object} selectedContact
 * @param {AppScope} appScope The opener's browsing scope; the drawer's links keep it.
 */
const openContactSidebar = (selectedContact, appScope) => {
  const scope = requireAppScope(appScope, 'openContactSidebar');
  return dispatch => {
    const open = () =>
      dispatch({
        type: UI_ACTIONS.OPEN_CONTACT_SIDEBAR,
        selectedContact,
        appScope: scope
      });
    const answer = resourceDrawerGuard.request(selectedContact);
    if (answer === true) {
      open();
    } else if (answer !== false) {
      answer.then(allowed => {
        if (allowed) open();
      });
    }
  };
};

const closeContactSidebar = () => {
  return { type: UI_ACTIONS.CLOSE_CONTACT_SIDEBAR };
};

/**
 * @param {object} selectedProduction
 * @param {{ appScope: AppScope, selectedSlotId?: number }} options
 *   `appScope`: the scope the drawer works in (see {@link requireAppScope}).
 */
const openProductionSidebar = (selectedProduction, options) => {
  return {
    type: UI_ACTIONS.OPEN_PRODUCTION_SIDEBAR,
    selectedProduction,
    options: {
      ...options,
      appScope: requireAppScope(options?.appScope, 'openProductionSidebar')
    }
  };
};

const closeProductionSidebar = () => {
  return { type: UI_ACTIONS.CLOSE_PRODUCTION_SIDEBAR };
};

/**
 * @param {object} selectedBooking
 * @param {{ appScope: AppScope, initialTab?: string }} options
 *   `appScope`: the scope the drawer works in (see {@link requireAppScope});
 *   send/template actions keep it.
 */
const openBookingSidebar = (selectedBooking, options) => {
  return {
    type: UI_ACTIONS.OPEN_BOOKING_SIDEBAR,
    selectedBooking,
    options: {
      ...options,
      appScope: requireAppScope(options?.appScope, 'openBookingSidebar')
    }
  };
};

const closeBookingSidebar = () => {
  return { type: UI_ACTIONS.CLOSE_BOOKING_SIDEBAR };
};

function normalizeAlertBody(body) {
  if (typeof body === 'string') return mapAlertError(body) || body;
  if (body && body.message) return mapAlertError(body.message) || body.message;
  if (body && body.Message && body.errors) {
    const errs = values(body.errors);
    if (errs && errs.length) return errs[0];
  }
  return 'Sorry something has gone wrong';
}

const displayAlert = (alertType, body, data) => {
  return function () {
    // this is our v1 bridge to v5 toast service
    const message = normalizeAlertBody(body);
    myToastService.open({ type: alertType, message });
  };
};

export {
  closeModal,
  openModal,
  displayAlert,
  openContactSidebar,
  closeContactSidebar,
  openProductionSidebar,
  closeProductionSidebar,
  openBookingSidebar,
  closeBookingSidebar
};
